import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import Table, { TableColumn } from '../components/Table';
import { SortState } from '../utils/tableUtils';
import {
  getMostExactScoreCatches,
  getMostWinnerCatches,
  getMostUpsetBonusCatches,
  getMostPointsLast6Games,
  getMostPerfectScores,
  getMostPointsFromGeneralBets,
  getMatchesMostPoints,
  getGroupStageLeader,
  getKnockoutLeader,
  UserBetStat,
  UserPointsStat,
  MatchSuccessStat,
} from '../api/funFactsApi';

const FunFacts: React.FC = () => {
  const [exactScoreCatches, setExactScoreCatches] = useState<UserBetStat[]>([]);
  const [winnerCatches, setWinnerCatches] = useState<UserBetStat[]>([]);
  const [upsetBonusCatches, setUpsetBonusCatches] = useState<UserBetStat[]>([]);
  const [pointsLast6, setPointsLast6] = useState<UserPointsStat[]>([]);
  const [perfectScores, setPerfectScores] = useState<UserBetStat[]>([]);
  const [generalBetsPoints, setGeneralBetsPoints] = useState<UserPointsStat[]>([]);
  const [matchesSuccess, setMatchesSuccess] = useState<MatchSuccessStat[]>([]);
  const [matchesSortState, setMatchesSortState] = useState<SortState<MatchSuccessStat>>({ key: 'success_pct', direction: 'desc' });
  const [groupStageLeader, setGroupStageLeader] = useState<UserPointsStat[]>([]);
  const [knockoutLeader, setKnockoutLeader] = useState<UserPointsStat[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAll = async () => {
      setIsLoading(true);
      setError('');
      try {
        const [exact, winner, upset, last6, perfect, generalPts, matchesPts, groupLeader, knockoutLeaderData] = await Promise.all([
          getMostExactScoreCatches(),
          getMostWinnerCatches(),
          getMostUpsetBonusCatches(),
          getMostPointsLast6Games(),
          getMostPerfectScores(),
          getMostPointsFromGeneralBets(),
          getMatchesMostPoints(),
          getGroupStageLeader(),
          getKnockoutLeader(),
        ]);
        setExactScoreCatches(exact);
        setWinnerCatches(winner);
        setUpsetBonusCatches(upset);
        setPointsLast6(last6);
        setPerfectScores(perfect);
        setGeneralBetsPoints(generalPts);
        setMatchesSuccess(matchesPts);
        setGroupStageLeader(groupLeader);
        setKnockoutLeader(knockoutLeaderData);
      } catch (err) {
        console.error('Error fetching fun facts:', err);
        setError('שגיאה בטעינת הנתונים.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchAll();
  }, []);

  const sortedMatchesSuccess = matchesSortState.direction === 'desc'
    ? matchesSuccess
    : [...matchesSuccess].reverse();

  const handleMatchesSort = (key: keyof MatchSuccessStat) => {
    setMatchesSortState(prev =>
      prev.key === key
        ? { key, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
        : { key, direction: 'desc' }
    );
  };

  const userBetStatColumns: TableColumn<UserBetStat>[] = [
    {
      key: 'first_name',
      title: 'שם',
      render: (item) => (
        <span className="font-medium">
          {item.first_name} {item.last_name}
        </span>
      ),
    },
    {
      key: 'count',
      title: 'כמות',
      render: (item) => (
        <span className="font-bold text-stake-blue">{item.count}</span>
      ),
    },
  ];

  const userPointsStatColumns: TableColumn<UserPointsStat>[] = [
    {
      key: 'first_name',
      title: 'שם',
      render: (item) => (
        <span className="font-medium">
          {item.first_name} {item.last_name}
        </span>
      ),
    },
    {
      key: 'points',
      title: 'נקודות',
      render: (item) => (
        <span className="font-bold text-stake-blue">{item.points}</span>
      ),
    },
  ];

  const matchSuccessStatColumns: TableColumn<MatchSuccessStat>[] = [
    {
      key: 'match',
      title: 'משחק',
      render: (item) => (
        <span className="font-medium">{item.match}</span>
      ),
    },
    {
      key: 'success_pct',
      title: '%',
      sortable: true,
      render: (item) => (
        <span className="font-bold text-stake-blue">{item.success_pct}%</span>
      ),
    },
  ];

  const highlightBetStat = (item: UserBetStat) => item.is_current_user;
  const highlightPointsStat = (item: UserPointsStat) => item.is_current_user;

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">טוען...</div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="text-red-500">{error}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div>
        <h1 className="text-2xl font-bold text-white mb-6">Fun Facts</h1>

        <div className="flex flex-wrap gap-6 mb-8 justify-center lg:justify-start">
          <div className="w-full lg:w-auto">
            <Table
              className="w-full sm:w-80"
              title="הכי הרבה מושלמים"
              withRanks={true}
              data={perfectScores}
              columns={userBetStatColumns}
              keyExtractor={(item) => item.user_id}
              highlightCondition={highlightBetStat}
              qualifyingPositions={1}
              emptyMessage="אין נתונים זמינים"
              collapsible={true}
              defaultCollapsed={false}
              collapsedShowHighlightedOnly={true}
              compact
            />
          </div>

          <div className="w-full lg:w-auto">
            <Table
              className="w-full sm:w-80"
              title="הכי הרבה תוצאות מדויקות"
              withRanks={true}
              data={exactScoreCatches}
              columns={userBetStatColumns}
              keyExtractor={(item) => item.user_id}
              highlightCondition={highlightBetStat}
              qualifyingPositions={1}
              emptyMessage="אין נתונים זמינים"
              collapsible={true}
              defaultCollapsed={false}
              collapsedShowHighlightedOnly={true}
              compact
            />
          </div>

          <div className="w-full lg:w-auto">
            <Table
              className="w-full sm:w-80"
              title="הכי הרבה כיוונים"
              withRanks={true}
              data={winnerCatches}
              columns={userBetStatColumns}
              keyExtractor={(item) => item.user_id}
              highlightCondition={highlightBetStat}
              qualifyingPositions={1}
              emptyMessage="אין נתונים זמינים"
              collapsible={true}
              defaultCollapsed={false}
              collapsedShowHighlightedOnly={true}
              compact
            />
          </div>

          <div className="w-full lg:w-auto">
            <Table
              className="w-full sm:w-80"
              title="נק׳ ב-6 המשחקים האחרונים"
              withRanks={true}
              data={pointsLast6}
              columns={userPointsStatColumns}
              keyExtractor={(item) => item.user_id}
              highlightCondition={highlightPointsStat}
              qualifyingPositions={1}
              emptyMessage="אין נתונים זמינים"
              collapsible={true}
              defaultCollapsed={false}
              collapsedShowHighlightedOnly={true}
              compact
            />
          </div>

          <div className="w-full lg:w-auto">
            <Table
              className="w-full sm:w-80"
              title="הכי הרבה נקודות מכלליים"
              withRanks={true}
              data={generalBetsPoints}
              columns={userPointsStatColumns}
              keyExtractor={(item) => item.user_id}
              highlightCondition={highlightPointsStat}
              qualifyingPositions={1}
              emptyMessage="אין נתונים זמינים"
              collapsible={true}
              defaultCollapsed={false}
              collapsedShowHighlightedOnly={true}
              compact
            />
          </div>

          <div className="w-full lg:w-auto">
            <Table
              className="w-full sm:w-80"
              title="מוביל שלב הבתים"
              withRanks={true}
              data={groupStageLeader}
              columns={userPointsStatColumns}
              keyExtractor={(item) => item.user_id}
              highlightCondition={highlightPointsStat}
              qualifyingPositions={1}
              emptyMessage="אין נתונים זמינים"
              collapsible={true}
              defaultCollapsed={false}
              collapsedShowHighlightedOnly={true}
              compact
            />
          </div>

          <div className="w-full lg:w-auto">
            <Table
              className="w-full sm:w-80"
              title="מוביל הפלייאוף"
              withRanks={true}
              data={knockoutLeader}
              columns={userPointsStatColumns}
              keyExtractor={(item) => item.user_id}
              highlightCondition={highlightPointsStat}
              qualifyingPositions={1}
              emptyMessage="אין נתונים זמינים"
              collapsible={true}
              defaultCollapsed={false}
              collapsedShowHighlightedOnly={true}
              compact
            />
          </div>

          <div className="w-full lg:w-auto">
            <Table
              className="w-full sm:w-80"
              title="אחוז ניחושים מוצלחים"
              withRanks={true}
              data={sortedMatchesSuccess}
              columns={matchSuccessStatColumns}
              keyExtractor={(item) => item.match_id}
              qualifyingPositions={1}
              emptyMessage="אין נתונים זמינים"
              collapsible={true}
              defaultCollapsed={false}
              sortState={matchesSortState}
              onSort={handleMatchesSort}
              compact
            />
          </div>

          <div className="w-full lg:w-auto">
            <Table
              className="w-full sm:w-80"
              title="הכי הרבה חוק ינקו"
              withRanks={true}
              data={upsetBonusCatches}
              columns={userBetStatColumns}
              keyExtractor={(item) => item.user_id}
              highlightCondition={highlightBetStat}
              qualifyingPositions={1}
              emptyMessage="אין נתונים זמינים"
              collapsible={true}
              defaultCollapsed={false}
              collapsedShowHighlightedOnly={true}
              compact
            />
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default FunFacts;
